import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { CONTENT, COLLECTIONS, IMAGES, PARTNERS } from "./mock";
import { api } from "./api";

const SiteContext = createContext(null);

const FALLBACK = { CONTENT, COLLECTIONS, IMAGES, PARTNERS };
const CACHE_KEY = "horns_content_cache_v1";

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data && data.CONTENT && data.COLLECTIONS && data.IMAGES) return data;
  } catch (e) {
    console.warn("Kunde inte läsa innehållscache:", e);
  }
  return null;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("horns_lang") : null;
    return saved === "en" || saved === "sv" ? saved : "sv";
  });

  const cached = typeof window !== "undefined" ? readCache() : null;
  const [site, setSite] = useState(cached || FALLBACK);
  // If we already have cached real content, we can render immediately (no flash).
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    localStorage.setItem("horns_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      if (data && data.CONTENT) {
        setSite(data);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch (e) { console.warn("Kunde inte spara innehållscache:", e); }
      }
    } catch (e) {
      console.warn("Kunde inte hämta innehåll, använder cache/fallback:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Safety: never stay in loading state indefinitely
    const timer = setTimeout(() => setLoading(false), 4000);
    return () => clearTimeout(timer);
  }, [refresh]);

  const toggle = () => setLang((l) => (l === "sv" ? "en" : "sv"));
  const t = site.CONTENT[lang] || CONTENT[lang];

  return (
    <SiteContext.Provider value={{
      lang, setLang, toggle, t, loading,
      images: site.IMAGES || IMAGES,
      collections: site.COLLECTIONS || COLLECTIONS,
      partners: site.PARTNERS || PARTNERS,
      refresh,
    }}>
      {children}
    </SiteContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
